import {fetchAvailabilityResult, getExportUrl} from '@/api/api';
import {useFavoriteProductStore} from '@/store/favorite.product.store';
import {useFilters} from '@/store/filter.store';
import {useRegionStore} from '@/store/region.store';
import {useProductTableStore} from '@/store/table.store';
import type {ExportWeeksObject} from '@/types/search/result';
import type {TableHeaders} from '@/types/table';
import {getUrlQueryString} from '@/utils/url.param.utils';
import {getSelectedWeek, type Product, setSelectedWeek} from 'dummen-shared';
import {defineStore} from 'pinia';

interface Availability {
	numberOfResults: number;
	availability: Array<Product>;
	tableHeaders: TableHeaders | null;
	exportWeeks: ExportWeeksObject | null;
	showSubstitutes: boolean;
}

export const useAvailability = defineStore('availabilityStore', {
	state: (): Availability => ({
		numberOfResults: 0,
		availability: [],
		tableHeaders: null,
		exportWeeks: null,
		showSubstitutes: true
	}),

	getters: {
		weeks(): Array<string> {
			if (!this.tableHeaders?.dynamic) {
				return [];
			}

			return Object.keys(this.tableHeaders.dynamic);
		},

		firstExportWeek(): string | null {
			return this.exportWeeks ? Object.keys(this.exportWeeks)[0] : null;
		},

		findWeekOrGetFirst() {
			return (value: string | null) => {
				return this.weeks ? (this.weeks.find(week => week === value) ?? this.weeks[0]) : null;
			};
		}
	},

	actions: {
		toggleSubstitutes() {
			this.showSubstitutes = !this.showSubstitutes;
		},

		getExportUrl() {
			const region = useRegionStore().getSelectedRegionUrlName();
			return getExportUrl(region, getUrlQueryString());
		},

		getQueryString(): string {
			return `${getUrlQueryString()}${useFavoriteProductStore().getFavoritesAsQueryParams()}`;
		},

		async getAvailabilityResult(): Promise<void> {
			const tableStore = useProductTableStore();
			tableStore.setLoading(true);

			const result = await fetchAvailabilityResult(this.getQueryString());

			if (result) {
				this.numberOfResults = result.count;
				this.availability = result.availability;
				this.tableHeaders = result.tableHeaders;
				this.exportWeeks = result.exportWeeks;

				const filters = useFilters();
				filters.setFilters(result.filters);
				filters.setSort(result.sort);
				filters.setPagination(result.pagination);
				filters.tooltips = result.tooltips;
				filters.setHideUnavailable(result.hideUnavailableProducts);
				setSelectedWeek(this.findWeekOrGetFirst(getSelectedWeek()));

				if (filters.expandedFilterName && !filters.expandedFilter()) {
					filters.resetExpandedFilter();
				}
			}

			tableStore.setLoading(false);
		}
	}
});
